import axiosInstance from "utils/axios";

/**
 * Service for handling authentication-related API operations
 */
const AuthService = {
  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} - Promise resolving to login response
   */
  login: async (email, password) => {
    try {
      const formData = new FormData();
      formData.append('username', email); // OAuth2 expects 'username' field
      formData.append('password', password);

      const response = await axiosInstance.post("/api/v1/auth/token", formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Store the access token
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }

      return response.data;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },

  /**
   * Confirm login with token from email
   * @param {string} token - Confirmation token from email
   * @returns {Promise<Object>} - Promise resolving to token response
   */
  confirmLogin: async (token) => {
    try {
      const response = await axiosInstance.get(`/api/v1/auth/confirm?token=${token}`);
      
      // Store the access token
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }

      return response.data;
    } catch (error) {
      console.error("Login confirmation error:", error);
      throw error;
    }
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} - Promise resolving to reset request response
   */
  requestPasswordReset: async (email) => {
    try {
      const response = await axiosInstance.post("/api/v1/auth/request-password-reset", { email });
      return response.data;
    } catch (error) {
      console.error("Password reset request error:", error);
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Promise resolving to reset response
   */
  resetPassword: async (token, newPassword) => {
    try {
      const response = await axiosInstance.post("/api/v1/auth/reset-password", {
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  },

  /**
   * Get current user information
   * @returns {Promise<Object>} - Promise resolving to user data
   */
  getCurrentUser: async () => {
    try {
      const response = await axiosInstance.get("/api/v1/users/me");
      return response.data;
    } catch (error) {
      console.error("Get current user error:", error);
      throw error;
    }
  },

  /**
   * Logout user
   * @returns {void}
   */
  logout: () => {
    // Remove stored tokens
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('access_token');
    
    // Redirect to login page
    window.location.href = '/authentication/sign-in';
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} - True if user has valid token
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    return !!token;
  },

  /**
   * Get stored access token
   * @returns {string|null} - Access token or null
   */
  getAccessToken: () => {
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  }
};

export default AuthService; 